<script setup>
import { useTemplateRef, watch } from 'vue';
import { useStore } from 'vuex';
import JobsearchSearchBar from './JobsearchSearchBar.vue';
import JobsearchDropdownBlockAutocompletions from './JobsearchDropdownBlockAutocompletions.vue';
import JobsearchDropdownBlockDefaultSuggestions from './JobsearchDropdownBlockDefaultSuggestions.vue';
import { queryTypes } from './constants.js';
import { splitQstring, useTwoWayBinding } from 'JIX/jobsearch/utils';
import { __ } from 'JIX/gettext.js';

defineProps({
    inputClasses: {
        type: String,
        default: 'form-control form-control-lg',
    },
    showBadgeTypes: {
        type: Array,
        default: () => queryTypes,
    },
    ariaLabel: {
        type: String,
        default: __('Hvilket job leder du efter?'),
    },
    placeholder: {
        type: String,
        default: __('Hvilket job leder du efter?'),
    },
});

const emit = defineEmits(['suggestionSelected']);

const store = useStore();
const showDropdown = useTwoWayBinding('showDropdown');
const userInput = useTwoWayBinding('userInput');
const autocompletions = useTemplateRef('autocompletions');
const defaultSuggestions = useTemplateRef('defaultSuggestions');
const searchbar = useTemplateRef('searchbar');

// For when the dropdown opens and the content of the autocompletion dropdown changes.
watch([showDropdown, () => autocompletions.value?.hasContent], ([newShowDropdown, autcompletionHasContent]) => {
    // When autocomplete has content the two other blocks should hide.
    if (autcompletionHasContent) {
        autocompletions.value.show = true;
        defaultSuggestions.value.show = false;
    // Fpr when the the dropdown is opened and autocompletion has no content.
    // This will also trigger when the dropdown is already opened and the autocompletion changes from content to no content.
    } else if (newShowDropdown && !autcompletionHasContent) {
        autocompletions.value.show = false;
        defaultSuggestions.value.show = true;
    }
});

defineExpose({
    showDropdown,
    handleUnhandledInput: ({ origin }) => handleUnhandledInput({ input: userInput.value, origin }),
    focusInput: () => searchbar.value?.focusInput(),
});


function handleUnhandledInput({ input, origin }) {
    splitQstring(input).forEach(value => store.dispatch('addParam', { key: 'q', value }));
    userInput.value = '';
    emit('suggestionSelected', { origin });
}

function enterHandler(event, { input }) {
    event.preventDefault();
    handleUnhandledInput({ input });
}

</script>

<template>
    <JobsearchSearchBar
        ref="searchbar"
        data-searchbar="general"
        v-model:show-dropdown="showDropdown"
        v-model:user-input="userInput"
        :input-classes="inputClasses"
        class="w-100"
        @unhandled-input="enterHandler"
        @suggestion-selected="$emit('suggestionSelected', $event)"
        :aria-label="ariaLabel"
        :placeholder="placeholder"
    >
        <JobsearchDropdownBlockAutocompletions
            ref="autocompletions"
        />
        <JobsearchDropdownBlockDefaultSuggestions
            ref="defaultSuggestions"
        />
    </JobsearchSearchBar>
</template>
